import { useEffect } from 'react'

import * as Sentry from '@sentry/react'
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType,
} from 'react-router-dom'

export const initSentry = () => {
	const sentryDsn = import.meta.env.VITE_PUBLIC_SENTRY_DSN

	if (!sentryDsn) {
		console.warn('Sentry DSN not configured - error tracking disabled')
		return
	}

	const environment = import.meta.env.VITE_APP_ENV || import.meta.env.MODE
	const isProduction = environment === 'production'

	Sentry.init({
		dsn: sentryDsn,
		environment,
		// TODO: Pending release implementation

		// Add debug mode in development to see what's happening
		debug: !isProduction,

		// Integration configurations
		integrations: [
			// React Router integration for automatic route tracking
			Sentry.reactRouterV7BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),

			// Browser tracing for performance
			Sentry.browserTracingIntegration({
				traceFetch: true,
				traceXHR: true,
			}),

			// Capture console errors and warnings
			Sentry.captureConsoleIntegration({
				levels: ['error'],
			}),

			Sentry.httpClientIntegration(),
		],

		// Send user data
		sendDefaultPii: true,

		// Ignore specific errors
		ignoreErrors: [
			// Browser extension errors
			/extensions\//i,
			/^chrome:\/\//i,
			/^moz-extension:\/\//i,

			// Network errors that are not actionable
			'NetworkError',
			'Network request failed',
			'Failed to fetch',

			// ResizeObserver errors (common browser quirk)
			'ResizeObserver loop limit exceeded',
			'ResizeObserver loop completed with undelivered notifications',

			// Common non-critical errors
			'Non-Error promise rejection captured',
			'AbortError: The operation was aborted',
			'cancelled',

			// PostHog errors
			/PostHog was initialized without a token/i,
			/\[PostHog\.js\].*PostHog was initialized without a token/i,

			// PostHog session recording (rrweb) SecurityError when accessing cross-origin iframes (e.g. Stripe)
			/Failed to read a named property 'removeEventListener' from 'Window'/i,

			// Amplitude errors — commonly triggered by ad blockers / Firefox ETP blocking
			// api.eu.amplitude.com; not actionable from application code.
			/amplitude/i,
		],

		// Denylist for URLs to ignore
		denyUrls: [
			// Browser extensions
			/extensions\//i,
			/^chrome:\/\//i,
			/^moz-extension:\/\//i,

			// Third-party scripts
			/google-analytics\.com/i,
			/googletagmanager\.com/i,

			// Amplitude — blocked by ad blockers / Firefox ETP; not actionable
			/amplitude\.com/i,
		],

		// Normalize URLs for better grouping
		normalizeDepth: 10,

		// Max breadcrumbs to keep
		maxBreadcrumbs: 50,

		// Attach stack traces to all messages
		attachStacktrace: true,

		// Sampling and filtering
		beforeSend(event, hint) {
			// Filter out specific errors you don't want to track
			const error = hint.originalException

			// Don't send errors from browser extensions
			if (error && error.toString().includes('extension://')) {
				return null
			}

			// Don't track AbortErrors (cancelled requests)
			if (error && (error as Error).name === 'AbortError') {
				return null
			}

			// Don't track PostHog initialization errors
			if (
				error &&
				(error.toString().includes('PostHog was initialized without a token') ||
					error.toString().includes('[PostHog.js]'))
			) {
				return null
			}

			// Don't track PostHog/rrweb SecurityErrors from cross-origin iframes (e.g. Stripe payment iframes)
			// rrweb session recording attempts to call removeEventListener on cross-origin iframe Windows,
			// which violates the Same-Origin Policy and throws a SecurityError that is not actionable.
			if (
				(error as Error)?.name === 'SecurityError' &&
				(error as Error)?.message?.includes('removeEventListener')
			) {
				return null
			}

			// Don't track Amplitude network errors (blocked by ad blockers / Firefox ETP)
			if (error && error.toString().toLowerCase().includes('amplitude')) {
				return null
			}

			// Filter Axios errors
			const axiosError = error as any
			if (axiosError && axiosError.isAxiosError) {
				// Filter network errors (offline/network issues)
				if (
					axiosError.code === 'ERR_NETWORK' ||
					axiosError.response?.status === 0
				) {
					return null
				}

				// Filter 401 Unauthorized errors (handled by auth logic)
				if (axiosError.response?.status === 401) {
					return null
				}
			}

			// Check error message in event as well
			if (
				event.message?.includes('PostHog was initialized without a token') ||
				event.message?.includes('[PostHog.js]')
			) {
				return null
			}

			// Ensure user context is attached (fix for scope isolation)
			if (!event.user || !event.user.id) {
				const currentScope = Sentry.getCurrentScope()
				const scopeUser = currentScope.getUser()
				if (scopeUser && scopeUser.id) {
					event.user = scopeUser
				}
			}

			// Log what we're sending (only in dev)
			if (!isProduction) {
				console.log('[Sentry] Sending event:', event.event_id, event.exception)
				console.log('[Sentry] User context:', event.user)
				console.log('[Sentry] Tags:', event.tags)
				console.log('[Sentry] Contexts:', event.contexts)
			}

			return event
		},
	})

	if (!isProduction) {
		console.log('[Sentry] Initialization complete')
	}
}
